import {Texture} from 'pixi.js';
import {CoreDI, DataStorage, lazyInject, ResourceLoader} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GameConfig, ILogic} from '@gongxigames/framework/slot/types';
import {ResourceManager} from '@gongxigames/framework/slot/services/ResourcesManager';
import {MatrixFilter} from '@gongxigames/framework/slot/services/logic';
import {ICheatPluginController, ICheatsModule} from '../../CheatModuleInterfaces';
import {makeReelsMatrix, objectHashMapToMap} from '../../helpers';

type CheatMatrixControllerData = {
    loading: boolean;
};

export class CheatMatrixController implements ICheatPluginController {
    @lazyInject(DI.Logic)
    protected readonly logic!: ILogic;

    @lazyInject(DI.ResourceManager)
    protected readonly resourceManager!: ResourceManager;

    @lazyInject(CoreDI.ResourceLoader)
    protected readonly resourceLoader!: ResourceLoader;

    @lazyInject(CoreDI.GameConfig)
    protected readonly gameConfig!: GameConfig;

    protected _module!: ICheatsModule;

    public matrix!: number[][];

    public store!: DataStorage<CheatMatrixControllerData>;

    public symbolsTextures: { [id: number]: any; } = {};

    public init(module: ICheatsModule): void {
        this._module = module;

        this.store = new DataStorage<CheatMatrixControllerData>({
            loading: true
        });

        const reelsMatrixSize = this.logic.getBaseGameReelsMatrixSize();

        this.matrix = makeReelsMatrix(reelsMatrixSize);
        this.resourceManager.loadStaticImages(['question_icon.png'], this.onLoadedResources.bind(this));
    }

    protected onLoadedResources(): void {
        const symbols = objectHashMapToMap<number, string>(this.gameConfig.reelsConfig.symbolsSprites);

        if (symbols.size > 0) {
            this.symbolsTextures[-1] = Texture.from('question_icon.png');

            for (const [id, name] of symbols.entries()) {
                this.symbolsTextures[id] = this.resourceLoader.getTexture(name);
            }
        }

        this.store.set('loading', false);
    }

    public addActionMatrixFilter(): void {
        const {matrix} = this;

        const isMatrixEmpty: boolean = matrix.every(reel => reel.every(id => id === -1));

        if (!isMatrixEmpty) {
            const newMatrix: any[][] = matrix.map(reel => reel.map(id => id > -1 ? id : undefined));
            const matrixFilter = new MatrixFilter('matrixFilter', newMatrix);
            const action = {
                id: `filter:${matrixFilter.name}`,
                apply: () => this._module.addArrayFilter(matrixFilter),
                reject: () => this._module.removeArrayFilter(matrixFilter)
            };

            this._module.addCheatAction(action);
        }
    }

    public rejectMatrixFilter(): void {
        const action = this._module.storage.data.cheatActions.find(action => action.id === 'filter:matrixFilter');

        if (action) {
            this._module.removeCheatAction(action);
        }

        this.matrix = this.matrix.map(reel => reel.map(() => -1));
    }

    public nextSymbol(prevSymbolID: number, reelIndex: number, rowIndex: number): void {
        const symbols = objectHashMapToMap(this.symbolsTextures);

        this.matrix[reelIndex][rowIndex] = prevSymbolID + 1 >= symbols.size - 1 ? -1 : prevSymbolID + 1;
    }

    public previousSymbol(prevSymbolID: number, reelIndex: number, rowIndex: number): void {
        this.matrix[reelIndex][rowIndex] = prevSymbolID - 1 < 0 ? -1 : prevSymbolID - 1;
    }
}
